import { ButtonHTMLAttributes, forwardRef } from "react";
import { VariantProps, cva } from "class-variance-authority";
import { cn } from "@/app/lib/utils";

/**
 * Button variants for different contexts and emphasis levels
 * - primary: Main call-to-action buttons (orange)
 * - secondary: Alternative or complementary actions (outlined)
 * - ghost: Subtle actions that don't need strong emphasis
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/80",
        primary:
          "bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/80",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/90 dark:bg-secondary dark:text-secondary-foreground dark:hover:bg-secondary/80",
        outline:
          "border border-input bg-transparent hover:bg-muted hover:text-foreground dark:border-input dark:hover:bg-muted/30 dark:hover:text-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground dark:hover:bg-muted/30 dark:hover:text-foreground",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 dark:bg-destructive dark:text-destructive-foreground dark:hover:bg-destructive/80",
        accent:
          "bg-accent text-accent-foreground hover:bg-accent/90 dark:bg-accent dark:text-accent-foreground dark:hover:bg-accent/80",
        link: "text-primary underline-offset-4 hover:underline dark:text-primary dark:hover:underline",
      },
      size: {
        sm: "h-9 px-3 text-sm",
        default: "h-10 px-4 py-2",
        lg: "h-12 px-8 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Optional custom className to apply to the button
   */
  className?: string;
  /**
   * Optional ribbon text to display in the top right corner
   */
  ribbonText?: string;
  /**
   * Optional ribbon color, defaults to destructive (red)
   */
  ribbonColor?: "destructive" | "primary" | "secondary" | "accent";
}

/**
 * Primary UI component for user interaction
 *
 * @example
 * ```tsx
 * // Primary button
 * <Button>Click me</Button>
 *
 * // Secondary button
 * <Button variant="secondary">Options</Button>
 *
 * // Ghost button
 * <Button variant="ghost">Cancel</Button>
 *
 * // Button with ribbon
 * <Button ribbonText="SOON">Coming Soon Feature</Button>
 * ```
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      ribbonText,
      ribbonColor = "destructive",
      ...props
    },
    ref
  ) => {
    return (
      <button
        className={cn(
          buttonVariants({ variant, size, className }),
          ribbonText && "relative overflow-hidden"
        )}
        ref={ref}
        {...props}
      >
        {props.children}
        {ribbonText && (
          <div className="absolute overflow-hidden top-0 right-0 w-16 h-16 z-10">
            <span
              className={cn(
                "absolute transform origin-top-right rotate-45 w-32 text-center py-1 text-xs font-bold text-white shadow-sm flex items-center justify-center",
                {
                  "bg-destructive": ribbonColor === "destructive",
                  "bg-primary": ribbonColor === "primary",
                  "bg-secondary": ribbonColor === "secondary",
                  "bg-accent": ribbonColor === "accent",
                }
              )}
              style={{ top: "60px", right: "-32px" }}
            >
              SOON!
            </span>
          </div>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
